import { format } from "date-fns"
import { ru } from "date-fns/locale/ru"
import { cn } from "@/shared/lib/utils"
import { StatusBadge, type StatusBadgeProps } from "@/shared/ui/status-badge"
import type { BroadcastStats, BroadcastStatus } from "../api/broadcasts-api"

export const BROADCAST_STATUS: Record<
	BroadcastStatus,
	{ label: string; tone: NonNullable<StatusBadgeProps["tone"]> }
> = {
	DRAFT: { label: "Черновик", tone: "muted" },
	SCHEDULED: { label: "Запланирована", tone: "info" },
	SENDING: { label: "Отправляется", tone: "warning" },
	DONE: { label: "Отправлена", tone: "success" },
	CANCELLED: { label: "Остановлена", tone: "danger" },
}

export function BroadcastStatusBadge({ status }: { status: BroadcastStatus }) {
	const meta = BROADCAST_STATUS[status]
	return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>
}

export const formatWhen = (value: string) =>
	format(new Date(value), "d MMMM, HH:mm", { locale: ru })

/**
 * Перенесённые без Telegram ID получают сообщение, как только найдём ID. В отчёте они
 * идут в «Отправлено» в темпе остальной рассылки, а не все разом в первую секунду.
 */
const waitingShown = (stats: BroadcastStats) => {
	const withId = stats.total - stats.waitingId - stats.noId
	const done = stats.sent + stats.blocked + stats.failed
	return withId > 0 ? Math.round((stats.waitingId * Math.min(done, withId)) / withId) : stats.waitingId
}

export const sentCount = (stats: BroadcastStats) => stats.sent + waitingShown(stats)

export const queuedCount = (stats: BroadcastStats) =>
	stats.pending + stats.waitingId - waitingShown(stats)

export const processedCount = (stats: BroadcastStats) =>
	sentCount(stats) + stats.blocked + stats.failed + stats.noId

/** Полоса отправки: отправлено, заблокировали, ошибки, ID не найден; остаток: в очереди. */
export function BroadcastProgress({
	stats,
	className,
}: {
	stats: BroadcastStats
	className?: string
}) {
	const total = Math.max(stats.total, 1)
	const segments = [
		{ key: "sent", value: sentCount(stats), className: "bg-emerald-400" },
		{ key: "blocked", value: stats.blocked, className: "bg-amber-400" },
		{ key: "failed", value: stats.failed, className: "bg-red-400" },
		{ key: "noId", value: stats.noId, className: "bg-muted-foreground/40" },
	]
	return (
		<div
			className={cn("flex h-1.5 overflow-hidden rounded-full bg-muted", className)}
			role="progressbar"
			aria-valuemin={0}
			aria-valuemax={stats.total}
			aria-valuenow={processedCount(stats)}
		>
			{segments.map((segment) =>
				segment.value > 0 ? (
					<span
						key={segment.key}
						className={cn("h-full transition-[width]", segment.className)}
						style={{ width: `${(segment.value / total) * 100}%` }}
					/>
				) : null,
			)}
		</div>
	)
}
